import { useMemo, useState } from "react";
import {
  MapPin,
  Building2,
  CheckCircle,
  Clock,
  Navigation,
  QrCode,
  Info,
  FileText,
  Eye,
  LogOut,
  AlertCircle
} from "lucide-react";
import "./ProgressTimeline.scss";
import JobInstructionsModal from "../JobInstructionModal/JobInstructionModal";

// Configuração dos passos com ícones
const TIMELINE_STEPS = [
  {
    key: "on_the_way",
    icon: Navigation,
    label: "A caminho",
    description: "Seu trajeto até a empresa já começou.",
  },
  {
    key: "arrived",
    icon: Building2,
    label: "Chegou",
    description: "Check-in realizado no local de trabalho.",
  },
  {
    key: "finished",
    icon: CheckCircle,
    label: "Finalizou",
    description: "A vaga foi concluída com sucesso.",
  },
];

// Metadados de cada estado
function getTimelineMeta(status, userType) {
  switch (status) {
    case "departed":
      return {
        currentIndex: 0,
        completed: [],
        primaryLabel: "Estou a caminho",
        primaryIcon: Navigation,

        secondaryLabel:
          userType === "Freelancer"
            ? "Ver instruções"
            : "Gerenciar instruções",

        secondaryIcon: userType === "Freelancer" ? Eye : FileText,
        secondaryAction: "instructions",

        helperText:
          userType === "Freelancer"
            ? "Clique em 'Estou a caminho' quando iniciar seu trajeto"
            : "Aguardando o freelancer iniciar o trajeto",

        showPrimaryButton: userType === "Freelancer",
      };
    case "on_the_way":
      return {
        currentIndex: 0,
        completed: [0],
        primaryLabel: "Escanear QR Code",
        primaryIcon: QrCode,

        secondaryLabel:
          userType === "Freelancer"
            ? "Ver instruções"
            : "Gerenciar instruções",

        secondaryIcon: userType === "Freelancer" ? Eye : FileText,
        secondaryAction: "instructions",
        secondaryIcon: FileText,
        secondaryAction: userType === "Freelancer" ? "instructions" : "addInstruction",

        helperText: userType === "Freelancer"
          ? "Escaneie o QR Code da empresa ao chegar"
          : "O freelancer está a caminho, aguarde a chegada",

        showPrimaryButton: userType === "Freelancer",
      };
    case "arrived":
      return {
        currentIndex: 1,
        completed: [0, 1],
        primaryLabel: "Finalizar turno",
        primaryIcon: LogOut,

        secondaryLabel: userType === "Freelancer" ? "Lembretes" : "Adicionar instrução",
        secondaryIcon: userType === "Freelancer" ? AlertCircle : FileText,
        secondaryAction: userType === "Freelancer" ? "instructions" : "addInstruction",

        helperText: userType === "Freelancer"
          ? "Check-in confirmado. Apenas a empresa pode finalizar o turno."
          : "O freelancer chegou. Finalize o turno quando o horário terminar.",

        showPrimaryButton: userType === "Company",
      };
    case "finished":
      return {
        currentIndex: 2,
        completed: [0, 1, 2],
        primaryLabel: "Vaga finalizada",
        primaryIcon: CheckCircle,

        secondaryLabel: "Resumo do turno",
        secondaryIcon: FileText,
        secondaryAction: "instructions",

        helperText: "Turno concluído com sucesso!",

        showPrimaryButton: true,
      };
    default:
      return {
        currentIndex: 0,
        completed: [],
        primaryLabel: "Estou a caminho",
        primaryIcon: Navigation,

        secondaryLabel:
          userType === "Freelancer"
            ? "Ver instruções"
            : "Gerenciar instruções",

        secondaryIcon: userType === "Freelancer" ? Eye : FileText,
        secondaryAction: "instructions",
        secondaryIcon: userType === "Freelancer" ? Eye : FileText,
        secondaryAction: userType === "Freelancer" ? "instructions" : "addInstruction",

        helperText: "Inicie o trajeto para começar",

        showPrimaryButton: true,
      };
  }
}

export default function ProgressTimeline({
  status = "pending",
  userType,
  onPrimaryAction,
  onSecondaryAction,
  job,
}) {
  const meta = useMemo(
    () => getTimelineMeta(status, userType),
    [status, userType]
  );

  const PrimaryIcon = meta.primaryIcon;
  const SecondaryIcon = meta.secondaryIcon;
  const [showInstructions, setShowInstructions] = useState(false);

  return (
    <div className="ip-timeline">
      {/* Header */}
      <div className="ip-timeline__header">
        <div>
          <div className="ip-timeline__eyebrow">
            <Clock size={14} />
            Progresso do turno
          </div>
          <h4 className="ip-timeline__title">Acompanhamento completo</h4>
        </div>
        <span className="ip-timeline__pill">
          <AlertCircle size={12} />
          Obrigatório
        </span>
      </div>

      {/* Helper text */}
      {meta.helperText && (
        <div className="ip-timeline__helper">
          <Info size={14} />
          <span>{meta.helperText}</span>
        </div>
      )}

      {/* Steps */}
      <div className="ip-timeline__steps">
        {TIMELINE_STEPS.map((step, index) => {
          const isCompleted = meta.completed.includes(index);
          const isActive = index === meta.currentIndex;
          const StepIcon = step.icon;

          return (
            <div
              key={step.key}
              className={`
                ip-timeline__step 
                ${isCompleted ? "is-complete" : ""} 
                ${isActive ? "is-active" : ""}
                ${!isCompleted && !isActive ? "is-pending" : ""}
              `}
            >
              <div className={`ip-timeline__icon ${isCompleted ? "is-complete" : ""}`}>
                {isCompleted ? (
                  <CheckCircle size={18} className="ip-timeline__check-icon" />
                ) : (
                  <StepIcon size={18} />
                )}
              </div>

              <div className="ip-timeline__content">
                <span className="ip-timeline__step-label">
                  {step.label}
                  {isActive && (
                    <span className="ip-timeline__step-badge">Atual</span>
                  )}
                </span>
                <p className="ip-timeline__step-desc">{step.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="ip-timeline__actions">
        {meta.showPrimaryButton && (
          <button
            className={`ip-timeline__btn ip-timeline__btn--primary ${!meta.showPrimaryButton ? "ip-timeline__btn--full" : ""}`}
            onClick={() => onPrimaryAction?.()}
          >
            <PrimaryIcon size={16} />
            {meta.primaryLabel}
          </button>
        )}

        <button
          className={`ip-timeline__btn ip-timeline__btn--secondary ${!meta.showPrimaryButton ? "ip-timeline__btn--full" : ""}`}
          onClick={() => {
            if (meta.secondaryAction === "instructions" || meta.secondaryAction === "addInstruction") {
              setShowInstructions(true);
            } else {
              onSecondaryAction?.();
            }
          }}
        >
          <SecondaryIcon size={16} />
          {meta.secondaryLabel}
        </button>
      </div>

      <JobInstructionsModal
        jobId={job?.id}
        userType={userType}
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
      />

    </div>
  );
}